let ctx = null;
const KEY = "nextg-sound";
let enabled =
  typeof window !== "undefined" && window.localStorage.getItem(KEY) === "on";
const listeners = new Set();

export const isSoundOn = () => enabled;

export const setSoundOn = (on) => {
  enabled = on;
  try {
    window.localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    /* storage unavailable — sound stays session-only */
  }
  listeners.forEach((fn) => fn(enabled));
};

export const onSoundChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// Background music state — ON by default (owner-requested ambient song),
// persisted; "off" sticks across visits. Browsers still require one user
// gesture before audio can actually start.
const MUSIC_KEY = "nextg-music";
let musicOn =
  typeof window !== "undefined" && window.localStorage.getItem(MUSIC_KEY) !== "off";
const musicListeners = new Set();

export const isMusicOn = () => musicOn;

export const setMusicOn = (on) => {
  musicOn = on;
  try {
    window.localStorage.setItem(MUSIC_KEY, on ? "on" : "off");
  } catch {
    /* storage unavailable — music stays session-only */
  }
  musicListeners.forEach((fn) => fn(musicOn));
};

export const onMusicChange = (fn) => {
  musicListeners.add(fn);
  return () => musicListeners.delete(fn);
};

// DJ-style reverse scratch + rewind zip — plays when the site crossfades
// from one page's song into the next. Tied to music (not UI sfx) state.
export const playDjReverse = () => {
  if (!musicOn) return;
  try {
    const c = getCtx();
    if (!c) return;
    const dur = 0.55;
    // reversed crash swell — noise that grows instead of decays
    const buffer = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(i / data.length, 2.2);
    }
    const src = c.createBufferSource();
    src.buffer = buffer;
    const filter = c.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.setValueAtTime(400, c.currentTime);
    filter.frequency.exponentialRampToValueAtTime(6000, c.currentTime + dur);
    const gain = c.createGain();
    gain.gain.setValueAtTime(0.0001, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.11, c.currentTime + dur);
    gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur + 0.08);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(c.destination);
    src.start();
    src.stop(c.currentTime + dur + 0.1);
    // vinyl rewind zip — falling pitch
    const osc = c.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(900, c.currentTime);
    osc.frequency.exponentialRampToValueAtTime(180, c.currentTime + 0.4);
    const og = c.createGain();
    og.gain.setValueAtTime(0.0001, c.currentTime);
    og.gain.exponentialRampToValueAtTime(0.045, c.currentTime + 0.05);
    og.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.42);
    osc.connect(og);
    og.connect(c.destination);
    osc.start();
    osc.stop(c.currentTime + 0.45);
  } catch {
    /* garnish only — never break navigation */
  }
};

const getCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

export const playWhoosh = () => {
  if (!enabled) return;
  try {
    const c = getCtx();
    if (!c) return;
    const dur = 0.7;
    const buffer = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    }
    const src = c.createBufferSource();
    src.buffer = buffer;
    const filter = c.createBiquadFilter();
    filter.type = "bandpass";
    filter.Q.value = 1.1;
    filter.frequency.setValueAtTime(320, c.currentTime);
    filter.frequency.exponentialRampToValueAtTime(2600, c.currentTime + dur * 0.55);
    filter.frequency.exponentialRampToValueAtTime(420, c.currentTime + dur);
    const gain = c.createGain();
    gain.gain.setValueAtTime(0.0001, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, c.currentTime + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(c.destination);
    src.start();
  } catch {
    // audio is a garnish — never break the transition
  }
};

// Short soft tick for menu open/close and tactile selections.
export const playBlip = (freq = 640) => {
  if (!enabled) return;
  try {
    const c = getCtx();
    if (!c) return;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, c.currentTime);
    osc.frequency.exponentialRampToValueAtTime(freq * 1.6, c.currentTime + 0.09);
    gain.gain.setValueAtTime(0.0001, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.07, c.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.16);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start();
    osc.stop(c.currentTime + 0.18);
  } catch {
    /* garnish only */
  }
};

// Techy UI chime for cursor click rips — clean two-tone bing, no hiss.
export const playZap = () => {
  if (!enabled) return;
  try {
    const c = getCtx();
    if (!c) return;
    const notes = [880, 1318.5]; // A5 -> E6 bing
    notes.forEach((freq, i) => {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, c.currentTime + i * 0.06);
      gain.gain.setValueAtTime(0.0001, c.currentTime + i * 0.06);
      gain.gain.exponentialRampToValueAtTime(0.09, c.currentTime + i * 0.06 + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + i * 0.06 + 0.3);
      osc.connect(gain);
      gain.connect(c.destination);
      osc.start(c.currentTime + i * 0.06);
      osc.stop(c.currentTime + i * 0.06 + 0.32);
    });
  } catch {
    /* garnish only */
  }
};

// Soft liquid pop for water ripples — short rounded blip.
export const playPop = () => {
  if (!enabled) return;
  try {
    const c = getCtx();
    if (!c) return;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(520, c.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, c.currentTime + 0.07);
    gain.gain.setValueAtTime(0.0001, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.06, c.currentTime + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.12);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start();
    osc.stop(c.currentTime + 0.14);
  } catch {
    /* garnish only */
  }
};
